/**
 * Dropdown
 *
 * Provides accessible navigation dropdowns with hover or click behavior.
 * Handles ARIA states, keyboard navigation, optional height animation,
 * and automatic focus management for both desktop hover and mobile click modes.
 *
 * Features:
 * - Hover mode: Desktop hover with full keyboard navigation (ArrowDown/Up, Escape)
 * - Click mode: Mobile/accordion click-only with simple keyboard (Space, Enter, Escape)
 * - Automatic ARIA state management (aria-expanded, aria-controls, role=menu)
 * - Optional height animation via data-hs-height wrapper
 * - Inert state for collapsed dropdowns (non-interactive)
 * - MutationObserver syncs ARIA with visual state changes
 * - Focus management for keyboard and screen reader users
 *
 * Structure:
 *   <div data-hs-nav-dropdown="wrapper" data-hs-nav-dropdown-type="hover">
 *     <button data-hs-clickable="button">Menu</button>
 *     <div data-hs-nav-dropdown="list" role="menu">
 *       <a href="#" data-hs-clickable="button" role="menuitem">Item 1</a>
 *       <a href="#" data-hs-clickable="button" role="menuitem">Item 2</a>
 *     </div>
 *   </div>
 */

import {
  querySelectorAll,
  querySelector,
  getSelector,
  globalConfig,
  animateHeight,
  setHeight,
} from '@utils';

interface EventHandler {
  element: Element;
  event: string;
  handler: EventListener;
  options?: AddEventListenerOptions;
}

interface ModuleConfig {
  attributes: {
    elements: Record<string, { primary: string; aliases?: string[] }>;
    properties?: Record<string, string>;
  };
}

// Module-scoped config (set during init)
let moduleConfig: ModuleConfig | null = null;

export function init(config: ModuleConfig) {
  // Store config at module scope for helper functions
  moduleConfig = config;

  const cleanup = {
    observers: [] as MutationObserver[],
    handlers: [] as EventHandler[],
  };

  const addObserver = (observer: MutationObserver): void => {
    cleanup.observers.push(observer);
  };
  const addHandler = (
    element: Element,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  function sanitizeForID(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  }

  function setupDropdown(
    addObserver: (observer: MutationObserver) => void,
    addHandler: (
      element: Element,
      event: string,
      handler: EventListener,
      options?: AddEventListenerOptions
    ) => void
  ): void {
    const dropdownWrappers = querySelectorAll(moduleConfig as any, 'wrapper');
    const hoverDropdowns: Element[] = []; // Track hover-type dropdowns for focus-loss handling

    dropdownWrappers.forEach((wrapper) => {
      const clickableSelector = getSelector(globalConfig.clickable as any, 'button');
      const toggle = wrapper.querySelector(clickableSelector);
      const dropdownList = querySelector(moduleConfig as any, 'list', wrapper);

      if (!toggle || !dropdownList) {
        console.warn('[dropdown] Dropdown wrapper missing required elements:', wrapper);
        return;
      }

      // Get dropdown type (hover or click)
      const type = wrapper.getAttribute('data-hs-nav-dropdown-type') || 'hover';

      // Check for visual height wrapper (contains data-hs-height="element")
      // If found, animate that instead of the list (list is for ARIA only)
      // If not found, no height animation (optional feature)
      const heightWrapper = wrapper.querySelector('[data-hs-height="element"]');
      const animationTarget = heightWrapper ? heightWrapper.parentElement : null;

      const toggleText = toggle.textContent?.trim() || 'dropdown';
      const sanitizedText = sanitizeForID(toggleText);
      const toggleId = `navbar-dropdown-${sanitizedText}-toggle`;
      const listId = `navbar-dropdown-${sanitizedText}-list`;

      toggle.id = toggleId;
      toggle.setAttribute('aria-haspopup', 'menu');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', listId);

      dropdownList.id = listId;
      dropdownList.setAttribute('role', 'menu');
      (dropdownList as HTMLElement).inert = true; // Initial state: hidden and non-interactive

      const menuItems = Array.from(dropdownList.querySelectorAll(clickableSelector));
      menuItems.forEach((item, index) => {
        item.setAttribute('role', 'menuitem');
        item.setAttribute('tabindex', '-1');

        // Add context for first item to help screen readers understand dropdown content
        if (index === 0) {
          const toggleText = toggle.textContent?.trim() || 'menu';
          const existingLabel = item.getAttribute('aria-label');
          if (!existingLabel) {
            item.setAttribute('aria-label', `${item.textContent?.trim()}, ${toggleText} submenu`);
          }
        }
      });

      let currentMenuItemIndex = -1;

      // Function to check if dropdown is open
      function isDropdownOpen() {
        return wrapper.classList.contains(globalConfig.classes.active);
      }

      // Update ARIA states based on current visual state
      function updateARIAStates() {
        const isOpen = isDropdownOpen();
        const wasOpen = toggle.getAttribute('aria-expanded') === 'true';

        // If dropdown is closing (was open, now closed), focus the toggle first
        if (wasOpen && !isOpen && dropdownList.contains(document.activeElement)) {
          (toggle as HTMLElement).focus();
        }

        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        (dropdownList as HTMLElement).inert = !isOpen; // Enable/disable interaction based on state
        menuItems.forEach((item) => {
          item.setAttribute('tabindex', isOpen ? '0' : '-1');
        });

        if (!isOpen) {
          currentMenuItemIndex = -1;
        }

        // Animate height wrapper (visual container, not ARIA list)
        animateHeight(animationTarget, isOpen, {
          duration: type === 'hover' ? 200 : 300,
          ease: 'power2.inOut',
        });
      }

      // Set initial height without animation
      setHeight(animationTarget, isDropdownOpen());

      // Set initial ARIA states
      updateARIAStates();

      // Monitor for class changes and update ARIA states
      const observer = new MutationObserver(() => {
        updateARIAStates();
      });

      observer.observe(wrapper, {
        attributes: true,
        attributeFilter: ['class'],
      });

      addObserver(observer);

      // ========================================
      // TYPE-SPECIFIC HANDLERS
      // ========================================

      if (type === 'hover') {
        // HOVER TYPE: Desktop behavior with hover + full keyboard navigation

        // Track this as a hover dropdown for focus-loss handling
        hoverDropdowns.push(wrapper);

        // Hover handlers
        const mouseenterHandler = () => {
          wrapper.classList.add('is-active');
        };

        const mouseleaveHandler = () => {
          wrapper.classList.remove('is-active');
        };

        addHandler(wrapper, 'mouseenter', mouseenterHandler);
        addHandler(wrapper, 'mouseleave', mouseleaveHandler);

        // Full keyboard navigation for toggle
        const toggleKeydownHandler = (e: KeyboardEvent): void => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();

            if (!isDropdownOpen()) {
              wrapper.classList.add(globalConfig.classes.active);

              // Focus first menu item after brief delay
              if (menuItems.length > 0) {
                setTimeout(() => {
                  currentMenuItemIndex = 0;
                  (menuItems[0] as HTMLElement).focus();
                }, 100);
              }
            }
          } else if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();

            if (!isDropdownOpen()) {
              wrapper.classList.add(globalConfig.classes.active);

              // Focus first menu item after brief delay
              if (menuItems.length > 0) {
                setTimeout(() => {
                  currentMenuItemIndex = 0;
                  (menuItems[0] as HTMLElement).focus();
                }, 100);
              }
            } else {
              wrapper.classList.remove(globalConfig.classes.active);
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();

            if (!isDropdownOpen()) {
              wrapper.classList.add(globalConfig.classes.active);

              // Focus last menu item after brief delay
              if (menuItems.length > 0) {
                setTimeout(() => {
                  currentMenuItemIndex = menuItems.length - 1;
                  (menuItems[currentMenuItemIndex] as HTMLElement).focus();
                }, 100);
              }
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();

            if (isDropdownOpen()) {
              wrapper.classList.remove(globalConfig.classes.active);
            }
          }
        };

        addHandler(toggle, 'keydown', toggleKeydownHandler);

        // Handle navigation within open dropdown (arrow keys)
        const dropdownKeydownHandler = (e: KeyboardEvent): void => {
          if (!isDropdownOpen()) return;
          if (!wrapper.contains(document.activeElement)) return;

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (currentMenuItemIndex < menuItems.length - 1) {
              currentMenuItemIndex++;
              (menuItems[currentMenuItemIndex] as HTMLElement).focus();
            }
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (currentMenuItemIndex === 0) {
              wrapper.classList.remove(globalConfig.classes.active);
              (toggle as HTMLElement).focus();
              currentMenuItemIndex = -1;
            } else if (currentMenuItemIndex > 0) {
              currentMenuItemIndex--;
              (menuItems[currentMenuItemIndex] as HTMLElement).focus();
            } else {
              (toggle as HTMLElement).focus();
              currentMenuItemIndex = -1;
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            wrapper.classList.remove('is-active');
          }
        };

        addHandler(wrapper, 'keydown', dropdownKeydownHandler);
      } else if (type === 'click') {
        // CLICK TYPE: Mobile/accordion behavior with click only + simple keyboard

        // Click handler to toggle
        const clickHandler = (e: MouseEvent): void => {
          e.preventDefault();
          wrapper.classList.toggle(globalConfig.classes.active);
        };

        addHandler(toggle, 'click', clickHandler);

        // Simple keyboard navigation (NO arrow keys)
        const toggleKeydownHandler = (e: KeyboardEvent): void => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            wrapper.classList.toggle(globalConfig.classes.active);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            if (isDropdownOpen()) {
              wrapper.classList.remove(globalConfig.classes.active);
            }
          }
        };

        addHandler(toggle, 'keydown', toggleKeydownHandler);
      }
    });

    // Close hover dropdowns when focus leaves (click dropdowns stay open)
    const focusinHandler = (e: FocusEvent): void => {
      hoverDropdowns.forEach((wrapper) => {
        if (wrapper.classList.contains('is-active') && !wrapper.contains(e.target as Node)) {
          wrapper.classList.remove('is-active');
        }
      });
    };

    // Only add handler if there are hover dropdowns
    if (hoverDropdowns.length > 0) {
      addHandler(document.body, 'focusin', focusinHandler);
    }
  }

  setupDropdown(addObserver, addHandler);

  return {
    result: 'dropdown initialized',
    destroy: () => {
      // Disconnect all observers
      cleanup.observers.forEach((obs) => obs.disconnect());
      cleanup.observers.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;

      // Clear module config
      moduleConfig = null;
    },
  };
}
